import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import _ from "lodash";
import { useEffect, useMemo } from "react";
import * as Yup from "yup";

import { GridInputs } from "../../../../../components/Functional";
import { Button } from "../../../../../components/ui";
import { INSTANCE_STATUS } from "../../../../../constants/instanceStatus";
import {
  CREATE_OFFER as createOfferMutation,
  UPDATE_OFFER as updateOfferMutation,
} from "../../../../../graphql/mutation/Offer";
import { FETCH_CURRENCIES } from "../../../../../graphql/query/Currency";
import { useEntities } from "../../../../../hooks/useEntities";
import useOfferStatus from "../../../../../hooks/useOfferStatus";

/**
 * This component is used both for editing and creating offers
 * Depending on the module it is used in, some fields will be passed as props
 * and the rest will be filled in by the user.
 * For example, in the Contact Offers page, the contact will be available
 * but the artwork will not, while in the Artwork Offers page, the artwork
 * will be available but the contact will not.
 *
 * @param {object} props.offer - the offer to edit, if any. If not, create
 * @param {function} props.closeModal
 * @param {boolean} props.isOpen
 * @returns {JSX.Element}
 */
const EditOffer = props => {
  const { offer, closeModal, isOpen } = props;
  const isEditing = Boolean(offer?.id);
  const { data: { currencies = [] } = {} } = useQuery(FETCH_CURRENCIES);
  const { offerStatuses, findOfferStatusByDescription } = useOfferStatus();
  const { entities } = useEntities();
  const [createOffer, createOptions] = useMutation(createOfferMutation(), {
    refetchQueries: ["FetchOffers", "FetchOffersV2", "FetchListItems"],
    awaitRefetchQueries: true,
    fetchPolicy: "network-only",
  });
  const [updateOffer, updateOptions] = useMutation(
    updateOfferMutation({ contact: {}, currency: {}, status: {} }),
    {
      refetchQueries: ["FetchOffers", "FetchOffersV2", "FetchListItems"],
      awaitRefetchQueries: true,
      fetchPolicy: "network-only",
    },
  );
  const loading = isEditing ? updateOptions.loading : createOptions.loading;
  const reset = isEditing ? updateOptions.reset : createOptions.reset;

  const statusId = useMemo(() => {
    const id = offer?.status?.id || offer?.statusId;

    if (!id) return findOfferStatusByDescription("Pending")?.id;

    return id;
  }, [offer, findOfferStatusByDescription]);

  const formik = useFormik({
    initialValues: {
      id: offer?.id,
      contactId: offer?.contact?.id || offer?.contactId,
      currencyId: offer?.currency?.id || offer?.currencyId,
      artworkId: offer?.artwork?.id || offer?.artworkId,
      statusId,
      holdId: offer?.hold?.id || offer?.holdId,
      amount: offer?.amount,
      tbiAmount: offer?.tbiAmount,
      date: offer?.date,
      dateClosed: offer?.dateClosed,
      holdDate: offer?.holdDate,
      tbiDate: offer?.tbiDate,
      notes: offer?.notes,
      priority: offer?.priority,
      user: offer?.user,
      projectCode: offer?.projectCode,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      contactId: Yup.number()
        .required("Please select a contact.")
        .typeError("Please select a contact."),
      artworkId: Yup.number()
        .required("Please select an artwork.")
        .typeError("Please select an artwork."),
      statusId: Yup.number()
        .required("Please select a status.")
        .typeError("Please select a status."),
      currencyId: Yup.number()
        .required("Please select a currency.")
        .typeError("Please select a currency."),
      holdId: Yup.number().positive().integer().nullable(),
      amount: Yup.number()
        .typeError("Please enter a valid amount.")
        .positive("must be a positive value")
        .nullable(),
      tbiAmount: Yup.number().when("statusId", ([statusId], schema) => {
        return statusId === findOfferStatusByDescription("To Be Invoiced")?.id
          ? schema.min(0).required("Please enter a valid amount")
          : schema.nullable();
      }),
    }),
    onSubmit: async values => {
      const removeFields = ["date", "dateClosed", "holdDate", "tbiDate"];
      const input = _.omitBy(_.omit(values, removeFields), _.isNil);

      const isClosed = ["Refused", "Invoiced", "Canceled"].some(status => {
        return values?.statusId === findOfferStatusByDescription(status)?.id;
      });

      // TODO: this should be done in the backend
      if (!values.dateClosed && isClosed) {
        input.dateClosed = new Date();
      }

      let action = createOffer;
      let key = "createOffer";

      // use values.id here to make sure which mutation to call
      if (values.id) {
        action = updateOffer;
        key = "updateOffer";
      }

      const response = await action({ variables: { input } });

      if (response?.data?.[key]?.success) {
        formik?.resetForm();
        closeModal();
      }

      reset();
    },
  });

  useEffect(() => {
    const { amount = 0, statusId, tbiAmount } = formik.values;

    if (!isOpen) return;

    if (
      !tbiAmount &&
      statusId === findOfferStatusByDescription("To Be Invoiced")?.id
    ) {
      // set tbiAmount with a 10% discount
      formik.setFieldValue("tbiAmount", Math.max(Math.ceil(amount * 0.9), 0));
    }
  }, [findOfferStatusByDescription, formik.values, isOpen]);

  useEffect(() => {
    if (isOpen) formik.validateForm();
  }, [isOpen]);

  const inputs = {
    className:
      "grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4 lg:gap-8",
    inputs: [
      {
        className: "grid grid-cols-1 gap-4",
        inputs: [
          { label: "Contact", name: "contactId", type: "contactSearch" },
          {
            label: "Artwork",
            name: "artworkId",
            redirect: true,
            type: "artworkSearch",
          },
          {
            label: "Status",
            name: "statusId",
            type: "multi-select",
            options: offerStatuses?.map(item => ({
              label: item?.description,
              value: item?.id,
            })),
            disabled: !isEditing,
          },
          {
            label: "Currency",
            name: "currencyId",
            type: "multi-select",
            isMulti: false,
            options: currencies?.map(currency => ({
              label: `${currency.description} (${currency.code})`,
              value: currency.id,
            })),
          },
          {
            name: "priority",
            options: [{ label: "Top Deal", value: -1 }],
            singleCheckbox: true,
            type: "checkbox",
          },
        ],
      },
      {
        className: "grid grid-cols-1 gap-4",
        inputs: [
          { label: "Amount", name: "amount", type: "number-currency" },
          {
            label: "TBI Amount",
            name: "tbiAmount",
            type: "number-currency",
            allowEmpty: true,
          },
          {
            label: "Hold",
            name: "holdId",
            redirect: true,
            // NOTE: we disable the hold field when creating an offer becuase of a missing database trigger.
            //       the better solution is to add the trigger to the database, but this is a "quick fix" to 
            //       allow offers to be created without a hold and then the trigger will fire correctly on 
            //       update. See the database trigger (offer.offer_bupdate) for more details.
            disabled: !isEditing || !formik?.values?.artworkId,
            type: "instanceSearch",
            defaultStatusIds: [
              INSTANCE_STATUS.AVAILABLE,
              INSTANCE_STATUS.ON_HOLD,
            ],
            filterInput: [
              ...(formik?.values?.artworkId
                ? [
                  {
                    _edition: {
                      artworkId: {
                        operator: "eq",
                        value: formik?.values?.artworkId,
                      },
                    },
                  },
                ]
                : []),
            ],
          },
          {
            label: "User",
            name: "user",
            type: "multi-select",
            options:
              entities?.edges?.map(item => {
                return { label: item.name, value: item.name };
              }) || [],
          },
        ],
      },
      {
        className: "grid grid-cols-1 gap-4",
        inputs: [
          {
            label: "Droite de Suite / ARR",
            name: "_",
            type: "arr",
            disabled: true,
          },
          { label: "Notes", name: "notes", rows: 5, type: "textarea" },
          {
            label: "Project Code",
            name: "projectCode",
            type: "text",
            disabled:
              formik.values.statusId !==
              findOfferStatusByDescription("To Be Invoiced")?.id,
          },
        ],
      },
      {
        className: "grid grid-cols-1 gap-4",
        inputs: [
          { label: "Date", name: "date", type: "date", disabled: true },
          {
            label: "Date Closed",
            name: "dateClosed",
            type: "date",
            disabled: true,
          },
          { label: "TBI Date", name: "tbiDate", type: "date", disabled: true },
          {
            label: "Hold Date",
            name: "holdDate",
            type: "date",
            disabled: true,
          },
        ],
      },
    ],
  };

  const inputProps = {
    formik,
    ...inputs,
  };

  const USER_ACTION = isEditing ? "Edit" : "Add";
  return (
    <div className="flex w-full flex-col p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="flex flex-1 flex-col text-2xl font-bold">
          {USER_ACTION} Offer
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-4">
          <Button
            action="default"
            className="sm:mr-4"
            label="Cancel"
            onClick={() => {
              closeModal();
              formik?.resetForm();
            }}
          />
          <Button
            label={`${USER_ACTION}${loading ? "ing" : ""}`}
            disabled={loading || !formik?.isValid || !formik?.dirty}
            onClick={formik.submitForm}
          />
        </div>
      </div>
      <div className="mt-8 pb-8">
        <GridInputs {...inputProps} />
      </div>
    </div>
  );
};

export default EditOffer;
